import React, { useState, useContext, useEffect } from 'react'
import Image from 'next/image';
import Script from 'next/script';
import { RequestContext } from '../../../context/RequestContext';
import { LocationContext } from '../../../context/LocationContext';
import {  reqAddressLatLng, getSgAddressByPostalCode } from '../../../service/location';
import { googleApiKey, s3Static } from '../../../utils/constants';
import { useRouter } from 'next/router';
import { SystemError } from '../../error/SystemError';
import ReactPlayer from 'react-player';
import { LoadingCover } from '../../commons/LoadingCover';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Home, AlertTriangle } from 'lucide-react';
import { PriceCard, FileUploadSection, ModernInput, ModernTextarea, NavigationButtons } from '../../commons/BookingFormComponents';
import { computePackagePrice } from '../../../utils/pt-package-price';
import { useBookingAttachments } from '../../../utils/useBookingAttachments';
import { useServiceSummaryAppend } from '../../../utils/useServiceSummaryAppend';
import { nextRequiredField } from '../../../utils/nextRequiredField';

// Animation variants - simplified to prevent layout shifts
const containerVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
            delayChildren: 0.05
        }
    }
};

const itemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.2
        }
    }
};

export const SubmitRequest = (props) => {
    const goToSection2 = props.goToSection2;
    const goToSection5 = props.goToSection5;
    // Previous step; older callers only pass goToSection2
    const goBack = props.goBack || goToSection2;

    const [ blkStreet, setBlkStreet ] = useState("")
    const [ unitNo, setUnitNo ] = useState("")
    const [ isLoadGoogleMap, setIsLoadGoogleMap ] = useState(false)
    const [ postalCode, setPostalCode ] = useState("")
    const [ errorMessage, setErrorMessage ] = useState("")
    const [ isAddressValid, setIsAddressValid ] = useState(false)
    const [ isValidating, setIsValidating ] = useState(false)

    const { requestCriteria, 
        requestDescription, 
        setRequestDescription, 
        setUserLocation, 
        requestType, 
        headline, 
        serviceCost, 
        setServiceCost,
        businessType,
        originalCost,
        setOriginalCost,
        totalDiscountPercent,
        selectedPackage,
        serviceDate,
        startTime
    } = useContext(RequestContext);

    // Recompute price when package or schedule changes (peak vs off-peak)
    const packagePrice = selectedPackage
        ? computePackagePrice(selectedPackage, serviceDate, startTime)
        : null;

    useEffect(() => {
        if (!selectedPackage) return;
        const { totalCost } = computePackagePrice(selectedPackage, serviceDate, startTime);
        setServiceCost(totalCost);
        setOriginalCost(totalCost);
    }, [selectedPackage, serviceDate, startTime, setServiceCost, setOriginalCost]);
    
    const { countryName, countryCd, location, countryCurrCd, setUserLatitude, setUserLongitude } = useContext(LocationContext)
    const [ isSearching, setIsSearching ] = useState(false);
    const [ isAichat, setIsAiChat ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 
    
    const router = useRouter();

    const { filesToAttach, onImageUpload, onVideoUpload, onRemove } = useBookingAttachments({
        onError: (content) => {
            setErrorContent(content)
            setIsSystemError(true)
        }
    })
    useServiceSummaryAppend(props.appendedSummaryRef)

    const validatePostalCode = async (postalCode) => {
        setIsValidating(true);
        setErrorMessage("");
        setBlkStreet("");
        
        // Use OneMap API for Singapore to get accurate block/street info
        if (countryCd === "sg") {
            try {
                const result = await getSgAddressByPostalCode(postalCode);
                setUserLatitude(result.latitude);
                setUserLongitude(result.longitude);
                setErrorMessage(result.address);
                setBlkStreet(result.blkStreet || result.address);
                setIsAddressValid(true);
            } catch (error) {
                setErrorMessage(error.message || "Address is incorrect");
                setBlkStreet("");
                setIsAddressValid(false);
            }
        } else {
            // Use Google API for other countries
            await reqAddressLatLng(countryName + " " + postalCode)
                .then(async (result) => {
                    setUserLatitude(result.latitude);
                    setUserLongitude(result.longitude);
                    setErrorMessage(result.address);
                    setBlkStreet(result.address);
                    setIsAddressValid(true);
                }, (errorCode) => {
                    setErrorMessage(errorCode);
                    setBlkStreet("");
                    setIsAddressValid(false);
                }
            );
        }
        setIsValidating(false);
    }

    const updatePostalCode = (e) => {
        const re = /^[0-9\b]+$/;

        if(e.target.value === "" || re.test(e.target.value)){
            setPostalCode(e.target.value);
            setErrorMessage("");
            setBlkStreet("");
            setIsAddressValid(false);
            e.target.setCustomValidity("");
            if(countryCd === "sg"){
                if(e.target.value.length === 6){
                    validatePostalCode(e.target.value);
                }
            }
        }
    } 

    const updateBlkStreet = (e) => {
        setBlkStreet(e.target.value);
    }

    const updateUnitNo = (e) => {
        setUnitNo(e.target.value);
    }

    const updateDescription = (e) =>{
        setRequestDescription(e.target.value);
    }
    
    const submitRequest = (e) => {
        e.preventDefault()
        try {            
            if(typeof gtag != "undefined") {
                switch (requestType) {
                    case "Aircon Services":                
                        gtag('event', 'conversion', {'send_to': 'AW-823304067/rDZvCNyc04sYEIO_yogD'});
                        break;   
                    case "Domestic Services":                
                        gtag('event', 'conversion', {'send_to': 'AW-823304067/PBaLCP7QyPgYEIO_yogD'});
                        break;      
                    case "Fitness Trainer":
                        gtag('event', 'conversion', {'send_to': 'AW-823304067/nvEnCMG8i-UCEIO_yogD'});
                        break;
                }
            }
        } catch (error) {
            console.log(error)
        }

        // Build full address from blkStreet and unitNo
        let fullAddress = blkStreet;
        if (unitNo) {
            fullAddress = `${blkStreet}, ${unitNo}`;
        }
        
        if (fullAddress) {
            setUserLocation(`Singapore ${postalCode}, ${fullAddress}`);
        } else {
            setUserLocation(`Singapore ${postalCode}`);
        }
        
        if(businessType === "job" || businessType === "hybrid"){
            goToSection5()
        } else {
            setIsSearching(true);
            setTimeout(() => {
                setIsSearching(false);
                goToSection5();
            }, 2000);
        }
    }
    
    useEffect(() => {        
        if(typeof google == "undefined") return
        setIsLoadGoogleMap(true)
    }, [setIsLoadGoogleMap])

    useEffect(() => {
        if(!location || requestDescription) return
        setUserLocation(location)
        const processRequestTypeRules = () => {
            if(requestType === "Fitness Trainer"){
                let remarks = ""
                for(let i = 0; i < requestCriteria.length; i++){
                    if(requestCriteria[i][5] != undefined){
                        if(requestCriteria[i][5].indexOf('(') > 0) {
                            remarks = "Personal training at " + requestCriteria[i][5].substr(0, requestCriteria[i][5].indexOf('(')) + ". "
                        } else {
                            remarks = "Personal training at " + requestCriteria[i][5] + ". "
                        }
                    }
                    if(requestCriteria[i][7] != undefined){
                        remarks = remarks + requestCriteria[i][7] + ". "
                    }
                }
                setRequestDescription(remarks)
            }
        }
        processRequestTypeRules()
    }, [location, setUserLocation, requestCriteria, requestType, setRequestDescription, requestDescription])

    useEffect(() => {
        if(router.query.booking){
            setRequestDescription(router.query.booking)
        }
    }, [router.query.booking, setRequestDescription])
    
    useEffect(() => {
        let date = new Date()

        if(date.getHours() > 18 || date.getHours() < 8){
            setIsAiChat(true)
        }
    }, [])

    // Block & street is asked for jobs once the postal code is complete
    const isStreetShown = (businessType === "job" || businessType === "hybrid") && postalCode.length === 6
    // The field to point at next (pulsing ring, no autofocus: focusing opens
    // the phone keyboard over the step before it has been read)
    const nextField = nextRequiredField([
        { name: "postal", done: postalCode.length === 6 },
        { name: "street", done: !isStreetShown || !!blkStreet.trim() },
        { name: "remarks", done: !!requestDescription?.trim() },
    ])

  return (
    <>
    <AnimatePresence mode="wait">
        {!isSearching ? (
            <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <form className="max-w-lg mx-auto px-3" onSubmit={(e) => submitRequest(e)}>
                    <motion.div 
                        className="space-y-5"
                        variants={containerVariants}
                        initial="initial"
                        animate="animate"
                    >
                        {/* Selected Package summary pill (above price card) */}
                        {selectedPackage && packagePrice && (
                            <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-2 -mb-2">
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100">
                                    {selectedPackage.trainerCategory} · {selectedPackage.totalServices} Sessions
                                </span>
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-50 text-gray-600 text-xs font-medium border border-gray-100">
                                    {selectedPackage.packageDuration} {selectedPackage.packageDurationUnit}
                                </span>
                                {packagePrice.isPeak ? (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">
                                        🕐 Peak +${packagePrice.peakPremium}/session
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-100">
                                        ☀️ Off-peak rate
                                    </span>
                                )}
                            </motion.div>
                        )}

                        {/* Price Card or Header */}
                        {serviceCost > 0 ? (
                            <PriceCard 
                                serviceCost={serviceCost}
                                originalCost={originalCost}
                                totalDiscountPercent={totalDiscountPercent}
                                currencyCode={countryCurrCd}
                            />
                        ) : (
                            <motion.div variants={itemVariants} className="text-center py-2">
                                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 text-orange-600 mb-2">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800">Your Location</h3>
                                <p className="text-xs text-gray-500">So that we can assign a {requestType === "Fitness Trainer" ? "trainer" : "team"} near you</p>
                            </motion.div>
                        )}
                        
                        {/* Postal Code Input */}
                        <ModernInput
                            type="text"
                            placeholder="Enter postal code"
                            value={postalCode}
                            onChange={updatePostalCode}
                            icon={MapPin}
                            minLength={6}
                            maxLength={6}
                            required
                            highlight={nextField === "postal"}
                            centerText
                            className="max-w-xs mx-auto"
                        />
                        
                        {/* Address validation message */}
                        <AnimatePresence>
                            {errorMessage && !isAddressValid && postalCode.length === 6 && (
                                <motion.div 
                                    className="text-center"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-sm">
                                        <AlertTriangle className="w-4 h-4" />
                                        {errorMessage}. Please enter address manually.
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        
                        {/* Block & Street and Unit - shown for job/hybrid when postal code is 6 digits */}
                        {isStreetShown && (
                            <div className="space-y-3">
                                {/* Block & Street - auto-populated if valid, manual input if not */}
                                <ModernInput
                                    type="text"
                                    placeholder="e.g. 123 Orchard Road"
                                    value={blkStreet}
                                    onChange={updateBlkStreet}
                                    icon={Home}
                                    label="Block & Street"
                                    required
                                    highlight={nextField === "street"}
                                />
                                
                                {/* Unit Number - optional */}
                                <ModernInput
                                    type="text"
                                    placeholder="e.g. #07-10"
                                    value={unitNo}
                                    onChange={updateUnitNo}
                                    icon={Home}
                                    label="Unit Number"
                                />
                            </div>
                        )}
                        
                        {/* Remarks */}
                        <ModernTextarea
                            placeholder="Brief description of your request..."
                            value={requestDescription}
                            onChange={updateDescription}
                            label="Remarks"
                            rows={3}
                            required
                            highlight={nextField === "remarks"}
                        />
                        
                        {/* File Upload (Handyman only) */}
                        {requestType === "Handyman Services" && (
                            <FileUploadSection
                                filesToAttach={filesToAttach}
                                onImageUpload={onImageUpload}
                                onVideoUpload={onVideoUpload}
                                onRemove={onRemove}
                            />
                        )}
                    </motion.div>
                    
                    {/* Back keeps the address and attachments entered */}
                    <NavigationButtons
                        onBack={() => goBack()}
                        onNextDisabled={!postalCode}
                        nextLabel={businessType === "job" ? "Continue" : "Next"}
                        nextId={null}
                    />
                </form>
            </motion.div>
        ) : (
            <motion.div 
                key="searching"
                className="text-center px-4 py-16 min-h-87.5 flex flex-col items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {/* Fixed size container for the image to prevent layout shifts */}
                <div className="w-45 h-45 mb-6 flex items-center justify-center">
                    <Image 
                        height={180} 
                        width={180} 
                        src={s3Static + "searching-fish.gif"} 
                        alt="Searching"
                        className="drop-shadow-lg"
                        priority
                    />
                </div>
                <h2 className="text-2xl font-bold text-orange-600">
                    Finding the best match...
                </h2>
                <p className="text-gray-500 mt-2">
                    Please wait a moment
                </p>
            </motion.div>
        )}
    </AnimatePresence>
        
        <Script 
            id="google-tagmanager-js"
            src="https://www.googletagmanager.com/gtag/js?id=AW-823304067"
            strategy="lazyOnload"
            onLoad={() => {              
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                
                gtag('config', 'AW-823304067');
                        
                window.gtag = gtag;
            }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
            {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
        <Script
            id="google-maps"
            src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&loading=async&language=en&libraries=places&callback=Function.prototype`}
            strategy="lazyOnload"
            onLoad={() => setIsLoadGoogleMap(true)}
        />                
        <Script 
          id="google-login-js"
          src="https://accounts.google.com/gsi/client"
          strategy="lazyOnload"
          onLoad={() => {              
              //initializeGoogleLogin();
          }} 
        />
        {/* Microsoft conversion tracking */}
        <Script id="microsoft-bing-conversion-tracking" strategy="lazyOnload">
           {`(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"97071180"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");`}
        </Script>
        {!isLoadGoogleMap &&
            <LoadingCover/>
        }
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
    </>
  )
}